import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, WalletCards } from 'lucide-react';
import api from '../../services/api';

export default function DepositReturnPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const depositId = searchParams.get('deposit') || searchParams.get('order_id') || searchParams.get('orderId');
  const queryTenant = searchParams.get('tenant');
  const slug = tenantSlug || queryTenant || null;
  const [message, setMessage] = useState('Checking your payment...');
  const [paid, setPaid] = useState(false);

  const appPath = useMemo(() => `${slug ? `/${slug}` : ''}/app?tab=wallet`, [slug]);

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (!depositId) {
        setMessage('No deposit reference found. Taking you back to wallet.');
        setTimeout(() => navigate(appPath, { replace: true }), 1600);
        return;
      }

      for (let attempt = 0; attempt < 6 && !cancelled; attempt += 1) {
        try {
          const res = await api.post(`/api/deposits/${depositId}/poll-status`);
          const status = String(res.data?.status || res.data?.gateway_status || '').toUpperCase();
          if (['APPROVED', 'SUCCESS', 'PAID', 'COMPLETED', 'SETTLED'].includes(status)) {
            setPaid(true);
            setMessage('Payment verified. Your balance is being refreshed.');
            setTimeout(() => navigate(appPath, { replace: true }), 1400);
            return;
          }
          if (['FAILED', 'REJECTED', 'CANCELLED', 'CANCELED', 'EXPIRED', 'TIMEOUT', 'TIMED_OUT'].includes(status)) {
            const expired = ['EXPIRED', 'TIMEOUT', 'TIMED_OUT'].includes(status);
            setMessage(expired ? 'Payment link expired. Returning to wallet.' : 'Payment was not completed. Returning to wallet.');
            setTimeout(() => navigate(appPath, { replace: true }), 1800);
            return;
          }
          setMessage('Still waiting for payment confirmation...');
        } catch (err) {
          if (err.response?.status === 401) {
            setMessage('Please log in again to refresh your wallet.');
            setTimeout(() => navigate(`${slug ? `/${slug}` : ''}/auth`, { replace: true }), 1800);
            return;
          }
          setMessage('Checking again...');
        }
        await new Promise(resolve => setTimeout(resolve, attempt < 2 ? 1800 : 3000));
      }

      if (!cancelled) {
        setMessage('Payment is still processing. Your wallet will update automatically.');
        setTimeout(() => navigate(appPath, { replace: true }), 1800);
      }
    }

    verify();
    return () => {
      cancelled = true;
    };
  }, [appPath, depositId, navigate, slug]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-[#070B12] px-5 text-white">
      <section className="w-full max-w-sm rounded-lg border border-white/10 bg-[#111827] p-6 text-center shadow-2xl">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
          {paid ? <CheckCircle2 className="h-7 w-7" /> : <WalletCards className="h-7 w-7" />}
        </div>
        <h1 className="text-xl font-bold">Payment Status</h1>
        <p className="mt-3 min-h-[48px] text-sm leading-6 text-slate-300">{message}</p>
        {!paid && (
          <Loader2 className="mx-auto mt-4 h-5 w-5 animate-spin text-emerald-300" aria-hidden />
        )}
      </section>
    </main>
  );
}
